/**
 * ReservationDetailsDialog — full FlightBridge reservation detail.
 *
 * Opened from the details button on a MovementCard. Surfaces the data the
 * FlightBridge feed supplies but the compact card hides: operator company +
 * primary contact, both legs (arrival + departure) with times/airports/
 * confidence, passenger/crew counts, requested services, triage, and the
 * flight reference. Read-only — the "Register" action defers to the caller.
 */

import {
  PlaneLanding,
  PlaneTakeoff,
  Building2,
  UserCircle2,
  Mail,
  Phone,
  Users,
} from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  SCHEDULE_CONFIDENCE_LABELS,
  SCHEDULE_CONFIDENCE_VARIANTS,
} from '@/types/aircraft-schedules'
import type { ScheduleItem } from './useTodayBoardData'

interface ReservationDetailsDialogProps {
  item: ScheduleItem | null
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Optional — when provided and the item is not yet registered, shows a Register action. */
  onRegister?: (item: ScheduleItem) => void
  /** Whether this item still needs registration (drives the Register button). */
  canRegister?: boolean
}

const TRIAGE_DOT_CLASS: Record<string, string> = {
  green: 'bg-emerald-500',
  amber: 'bg-amber-500',
  red: 'bg-red-500',
}

const SERVICE_LABELS: Record<string, string> = {
  fuel: 'Fuel',
  gpu: 'GPU',
  lav: 'Lav',
  catering: 'Catering',
  rental_car: 'Car',
  hangar_request: 'Hangar',
}

function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '—'
  const d = new Date(iso)
  return d.toLocaleString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function formatCondition(c?: string | null): string | null {
  return c ? c.replace(/_/g, ' ') : null
}

/** Resolve the arrival and departure leg display from an item + its paired leg. */
function resolveLegs(item: ScheduleItem) {
  if (item.movementType === 'arriving') {
    return {
      arrival: { time: item.scheduledTime, airport: item.airportIcao, confidence: item.confidence },
      departure: { time: item.pairedScheduledTime, airport: item.pairedAirportIcao },
    }
  }
  return {
    arrival: { time: item.pairedScheduledTime, airport: item.pairedAirportIcao, confidence: null },
    departure: { time: item.scheduledTime, airport: item.airportIcao },
  }
}

function InfoRow({ icon, label, value }: { icon: React.ReactNode; label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-start gap-2 text-sm">
      <span className="mt-0.5 text-muted-foreground shrink-0">{icon}</span>
      <span className="text-muted-foreground w-24 shrink-0">{label}</span>
      <span className="font-medium break-words">{value}</span>
    </div>
  )
}

export function ReservationDetailsDialog({
  item,
  open,
  onOpenChange,
  onRegister,
  canRegister,
}: ReservationDetailsDialogProps) {
  if (!item) return null

  const aircraft = item.data.aircraft
  const owner = item.owner
  const contact = owner?.primaryContact
  const contactName = contact
    ? [contact.firstName, contact.lastName].filter(Boolean).join(' ').trim()
    : null
  const legs = resolveLegs(item)
  const services = item.services ?? []

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {item.triageColor && (
              <span
                className={`h-2.5 w-2.5 rounded-full shrink-0 ${TRIAGE_DOT_CLASS[item.triageColor] ?? ''}`}
                title={`Triage: ${item.triageColor}`}
              />
            )}
            <span className="font-bold">{aircraft.tailNumber}</span>
            <span className="text-sm font-normal text-muted-foreground">
              {aircraft.typeCode ?? [aircraft.manufacturer, aircraft.model].filter(Boolean).join(' ')}
            </span>
          </DialogTitle>
          <DialogDescription>
            {item.flightReference ? `Ref ${item.flightReference}` : 'Reservation detail'}
            {item.providerCode ? ` · ${item.providerCode}` : ''}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-1">
          {/* ── Operator / company ── */}
          <section className="space-y-1.5">
            <h4 className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              Operator
            </h4>
            {owner ? (
              <>
                <InfoRow
                  icon={owner.clientType === 'individual' ? <UserCircle2 className="h-4 w-4" /> : <Building2 className="h-4 w-4" />}
                  label="Company"
                  value={owner.displayName ?? '—'}
                />
                {owner.emailPrimary && (
                  <InfoRow icon={<Mail className="h-4 w-4" />} label="Email" value={
                    <a href={`mailto:${owner.emailPrimary}`} className="text-blue-600 hover:underline">{owner.emailPrimary}</a>
                  } />
                )}
                {owner.phonePrimary && (
                  <InfoRow icon={<Phone className="h-4 w-4" />} label="Phone" value={
                    <a href={`tel:${owner.phonePrimary}`} className="text-blue-600 hover:underline">{owner.phonePrimary}</a>
                  } />
                )}
              </>
            ) : (
              <p className="text-sm text-muted-foreground">No operator on file.</p>
            )}
          </section>

          {/* ── Primary contact (dispatcher) ── */}
          {contact && (
            <section className="space-y-1.5">
              <h4 className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                Contact
              </h4>
              <InfoRow
                icon={<UserCircle2 className="h-4 w-4" />}
                label={contact.jobTitle ?? 'Contact'}
                value={contactName || '—'}
              />
              {contact.emailPrimary && (
                <InfoRow icon={<Mail className="h-4 w-4" />} label="Email" value={
                  <a href={`mailto:${contact.emailPrimary}`} className="text-blue-600 hover:underline">{contact.emailPrimary}</a>
                } />
              )}
              {contact.phoneMobile && (
                <InfoRow icon={<Phone className="h-4 w-4" />} label="Mobile" value={
                  <a href={`tel:${contact.phoneMobile}`} className="text-blue-600 hover:underline">{contact.phoneMobile}</a>
                } />
              )}
            </section>
          )}

          {/* ── Itinerary ── */}
          <section className="space-y-1.5">
            <h4 className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              Itinerary
            </h4>
            <InfoRow
              icon={<PlaneLanding className="h-4 w-4" />}
              label="Arrival"
              value={
                <span className="flex items-center gap-2">
                  {formatDateTime(legs.arrival.time)}
                  {legs.arrival.airport && <span className="text-muted-foreground">from {legs.arrival.airport}</span>}
                  {legs.arrival.confidence && (
                    <Badge variant={SCHEDULE_CONFIDENCE_VARIANTS[legs.arrival.confidence]} className="text-[9px] h-4 px-1.5">
                      {SCHEDULE_CONFIDENCE_LABELS[legs.arrival.confidence]}
                    </Badge>
                  )}
                </span>
              }
            />
            <InfoRow
              icon={<PlaneTakeoff className="h-4 w-4" />}
              label="Departure"
              value={
                <span className="flex items-center gap-2">
                  {formatDateTime(legs.departure.time)}
                  {legs.departure.airport && <span className="text-muted-foreground">to {legs.departure.airport}</span>}
                </span>
              }
            />
            {(item.passengerCount != null || item.crewCount != null) && (
              <InfoRow
                icon={<Users className="h-4 w-4" />}
                label="On board"
                value={`${item.passengerCount ?? 0} pax · ${item.crewCount ?? 0} crew`}
              />
            )}
          </section>

          {/* ── Services ── */}
          {services.length > 0 && (
            <section className="space-y-1.5">
              <h4 className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                Requested Services
              </h4>
              <div className="flex flex-wrap gap-1.5">
                {services.map((s, i) => {
                  const type = String(s.type ?? '')
                  const label = SERVICE_LABELS[type] ?? type
                  const gallons = type === 'fuel' && s.gallons ? ` ${s.gallons}g` : ''
                  return (
                    <Badge key={i} variant="secondary" className="text-[10px] font-normal">
                      {label}{gallons}
                    </Badge>
                  )
                })}
              </div>
            </section>
          )}

          {/* ── Triage / notes ── */}
          {(item.triageCondition || item.notes) && (
            <section className="space-y-1.5">
              <h4 className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                Notes
              </h4>
              {item.triageCondition && (
                <p className="text-sm capitalize">{formatCondition(item.triageCondition)}</p>
              )}
              {item.notes && <p className="text-sm text-muted-foreground">{item.notes}</p>}
            </section>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
          {canRegister && onRegister && (
            <Button
              onClick={() => {
                onRegister(item)
                onOpenChange(false)
              }}
            >
              Register
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
